import type { FormEvent } from "react";

import { Button, Dialog, Flex, Text, TextField } from "@radix-ui/themes";

import type { WatchlistNameDialogMode } from "./WatchlistNameDialog.controller";

export interface WatchlistNameDialogPresentationProps {
  open: boolean;
  mode: WatchlistNameDialogMode;
  name: string;
  error: string | null;
  isSubmitting: boolean;
  onOpenChange: (open: boolean) => void;
  onNameChange: (value: string) => void;
  onSubmit: (event?: FormEvent<HTMLFormElement>) => Promise<void>;
}

const TITLES: Record<WatchlistNameDialogMode, string> = {
  create: "Create a watchlist",
  rename: "Rename watchlist",
};

const SUBMIT_LABELS: Record<WatchlistNameDialogMode, string> = {
  create: "Create",
  rename: "Save",
};

export const WatchlistNameDialogPresentation = ({
  open,
  mode,
  name,
  error,
  isSubmitting,
  onOpenChange,
  onNameChange,
  onSubmit,
}: WatchlistNameDialogPresentationProps) => {
  const isNameBlank = name.trim().length === 0;

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Content maxWidth="420px">
        <Dialog.Title>{TITLES[mode]}</Dialog.Title>
        <Dialog.Description size="2" mb="4" color="gray">
          Give this watchlist a name. You can change it later.
        </Dialog.Description>

        <form onSubmit={onSubmit}>
          <TextField.Root
            aria-label="Watchlist name"
            autoFocus
            value={name}
            onChange={(event) => onNameChange(event.target.value)}
          />

          {error ? (
            <Text mt="3" size="2" color="red" role="alert">
              {error}
            </Text>
          ) : null}

          <Flex gap="3" mt="4" justify="end">
            <Dialog.Close>
              <Button type="button" variant="soft" color="gray">
                Cancel
              </Button>
            </Dialog.Close>
            <Button type="submit" disabled={isSubmitting || isNameBlank}>
              {SUBMIT_LABELS[mode]}
            </Button>
          </Flex>
        </form>
      </Dialog.Content>
    </Dialog.Root>
  );
};
